export class IdentifyImagePreview {
  private cards: HTMLElement[] = [];
  private overlay: HTMLElement | null = null;
  private overlayImg: HTMLImageElement | null = null;
  private hoverTimers = new Map<HTMLElement, number>();
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private clickHandler: ((e: MouseEvent) => void) | null = null;
  private hideTimeoutId: number | null = null;

  constructor() {
    this.overlay = document.getElementById('identify-image-preview');
    this.overlayImg = this.overlay?.querySelector('.identify-image-preview-img') as HTMLImageElement | null;

    this.cards = Array.from(document.querySelectorAll<HTMLElement>('.identify-card'));
    if (!this.overlay || !this.overlayImg || this.cards.length === 0) return;

    this.cards.forEach((card) => {
      card.addEventListener('pointerenter', (e) => this.onEnter(card, e));
      card.addEventListener('pointerleave', () => this.onLeave(card));
    });

    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.hide();
    };
    document.addEventListener('keydown', this.keydownHandler);

    this.clickHandler = () => {
      // close on any click inside overlay
      if (!this.overlay) return;
      if (this.overlay.getAttribute('aria-hidden') === 'false') this.hide();
    };
    this.overlay.addEventListener('click', this.clickHandler);
  }

  private onEnter(card: HTMLElement, e: PointerEvent): void {
    // Hover-only interaction (avoid touch/pen)
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (this.hoverTimers.has(card)) return;

    const timer = window.setTimeout(() => {
      this.hoverTimers.delete(card);
      const bg = card.querySelector<HTMLElement>('.identify-card-bg');
      const bgImage = bg ? getComputedStyle(bg).backgroundImage : '';
      const url = this.extractUrl(bgImage);
      if (url) this.show(url);
    }, 1000);

    this.hoverTimers.set(card, timer);
  }

  private onLeave(card: HTMLElement): void {
    const timer = this.hoverTimers.get(card);
    if (timer) {
      clearTimeout(timer);
      this.hoverTimers.delete(card);
    }
  }

  private extractUrl(bgImage: string): string | null {
    // background-image: url("...") or url(...)
    const match = bgImage.match(/url\((\"|')?(.*?)\1\)/);
    return match?.[2] ?? null;
  }

  private show(url: string): void {
    if (!this.overlay || !this.overlayImg) return;
    this.overlayImg.src = url;
    this.overlay.setAttribute('aria-hidden', 'false');
    this.overlay.classList.remove('identify-image-preview-hiding');
    // Restart animation cleanly
    this.overlay.classList.remove('identify-image-preview-visible');
    requestAnimationFrame(() => {
      this.overlay?.classList.add('identify-image-preview-visible');
    });
  }

  private hide(): void {
    if (!this.overlay || !this.overlayImg) return;
    if (this.overlay.getAttribute('aria-hidden') === 'true') return;
    this.overlay.classList.add('identify-image-preview-hiding');
    if (this.hideTimeoutId !== null) window.clearTimeout(this.hideTimeoutId);
    this.hideTimeoutId = window.setTimeout(() => {
      if (!this.overlay || !this.overlayImg) return;
      this.overlay.setAttribute('aria-hidden', 'true');
      this.overlay.classList.remove('identify-image-preview-visible');
      this.overlay.classList.remove('identify-image-preview-hiding');
      this.overlayImg.src = '';
      this.hideTimeoutId = null;
    }, 230);
  }

  dispose(): void {
    this.hoverTimers.forEach((t) => clearTimeout(t));
    this.hoverTimers.clear();
    if (this.hideTimeoutId !== null) window.clearTimeout(this.hideTimeoutId);
    if (this.keydownHandler) document.removeEventListener('keydown', this.keydownHandler);
    if (this.overlay && this.clickHandler) this.overlay.removeEventListener('click', this.clickHandler);
    this.cards = [];
    this.overlay = null;
    this.overlayImg = null;
    this.keydownHandler = null;
    this.clickHandler = null;
    this.hideTimeoutId = null;
  }
}

